import {
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import React, { useEffect, useMemo, useState } from 'react'

import { getKekaAttendanceWeek } from 'api/kekaAttendance'
import { PunctualityDialog } from 'app/pages/Audits/TeacherScorecard/Punctuality'
import { dayFor, missedClass } from 'app/pages/Audits/TeacherScorecard/scoring'
import useCalendarDaysOff from 'app/pages/Audits/TeacherScorecard/useCalendarDaysOff'

// Teacher > Attendance (2026-09-30). A date range of Keka's Daily Performance
// report per staff member: status, clock-in, minutes late. The API returns
// Keka's raw facts; the rules that turn them into "late" / "absent" live
// here, in one place, and are spelled out in the (i) tooltip.

// Late = clocked in after the shift start in Keka, minutes counted from the
// shift start (Satya, 28 Oct: "no buffer needed for anyone including 8:30
// ones"; 9:00 shift, in at 9:17 -> 17 min late). Keka's 8:30 shift already
// starts at 8:46 since 22 Oct, so no grace is added on top here.
export const GRACE_MINUTES = 0
export const lateAfterGrace = (v) =>
  Math.max(0, ((v && v.late) || 0) - GRACE_MINUTES)
// Departments whose staff actually clock in on Keka. HQ mostly does not (so
// they read "absent" every day) and is not shown on this page at all.
const FIELD_DEPARTMENTS = new Set([
  'Preschool',
  'Centre',
  'Teacher Community',
  'Explore'
])
// Roles the Head of Preschool reviews here, shown whatever department Keka
// files them under (2026-10-22): an Admissions Manager can sit in Sales.
const FIELD_ROLES =
  /centre director|admissions manager|early years|public speaking teacher/i
// HQ (Keka cost centre "Corporate") is left off the page whatever the role
// (Satya, 26 Oct): the page is for centre staff.
const onPage = (e) =>
  e.cc !== 'Corporate' &&
  (FIELD_DEPARTMENTS.has(e.dept) || FIELD_ROLES.test(e.job || ''))
const LEAVE = {
  CL: 'Casual leave',
  SL: 'Sick leave',
  PL: 'Paid leave',
  UL: 'Unpaid leave',
  L: 'Leave',
  ML: 'Maternity leave',
  PT: 'Paternity leave',
  BL: 'Bereavement leave',
  FL: 'Floater leave',
  SPL: 'Special leave',
  CO: 'Comp off',
  SBTL: 'Sabbatical'
}
const NO_CENTRE = 'No centre in Keka'
// Time frames. All of them end on (or count back from) the latest day Keka
// has sent, not on today — Keka's export always stops at yesterday.
const RANGES = [
  ['7', 'Last 7 days'],
  ['14', 'Last 14 days'],
  ['30', 'Last 30 days'],
  ['month', 'This month'],
  ['lastMonth', 'Last month'],
  ['custom', 'Custom dates']
]
// The server refuses anything longer (two calendar months).
const MAX_DAYS = 62
// Past this many days the grid switches to one narrow square per day.
const WIDE_DAYS = 7
const SHORT = { P: 'P', late: 'L', MS: 'P', A: 'A', L: 'Lv', WO: '–', X: '' }

const C = {
  navy: '#282E3C',
  line: '#ECE6DE',
  ink2: 'rgba(0,0,0,.56)',
  orange: '#F25E35',
  present: '#35A257',
  presentBg: '#E8F5EC',
  absent: '#8E1F35',
  absentBg: '#F8E7EB',
  leave: '#3172C6',
  leaveBg: '#E8F0FA',
  late: '#B4441C',
  lateBg: '#FCEDE6',
  off: '#9A9A9A'
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: theme.spacing(2)
  },
  info: { color: C.ink2, fontSize: 20, cursor: 'help' },
  bar: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  range: { minWidth: 150, background: '#fff' },
  date: { width: 160, background: '#fff' },
  step: { display: 'flex', alignItems: 'center', gap: 4 },
  arrow: { border: `1px solid ${C.line}`, background: '#fff' },
  hint: { color: C.ink2, fontSize: 12, marginBottom: 12 },
  // Long ranges: the grid is wider than the page, so the card scrolls both
  // ways and the header + name column stick inside it.
  scroll: { overflow: 'auto', maxHeight: '72vh' },
  mini: {
    borderRadius: 4,
    height: 26,
    lineHeight: '24px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700
  },
  thDay: { textAlign: 'center', padding: '6px 2px', lineHeight: 1.25 },
  tdMini: { padding: '4px 2px' },
  stick: { position: 'sticky', left: 0, zIndex: 1, background: '#fff' },
  select: { minWidth: 190, background: '#fff' },
  headSearch: {
    background: '#fff',
    borderRadius: 4,
    '& input': { fontSize: 12.5, padding: '6px 8px 6px 0' },
    '& input::placeholder': { color: '#1f2430', opacity: 0.75, fontWeight: 600 }
  },
  tileN: { fontSize: 24, fontWeight: 700 },
  tileL: { color: C.ink2, fontSize: 12 },
  // No overflow on the card: the page itself scrolls, and the header row
  // sticks to the top of the window (a scrolling card would trap it).
  card: { border: `1px solid ${C.line}`, borderRadius: 10 },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    tableLayout: 'fixed'
  },
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: C.navy,
    color: '#fff',
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'left',
    padding: '9px 6px',
    boxShadow: '0 2px 4px rgba(0,0,0,.12)',
    '&:first-child': { paddingLeft: 12, borderTopLeftRadius: 10 },
    '&:last-child': { borderTopRightRadius: 10 }
  },
  td: {
    borderBottom: `1px solid ${C.line}`,
    padding: '5px 6px',
    verticalAlign: 'middle'
  },
  who: { paddingLeft: 12 },
  name: { fontWeight: 600, fontSize: 14 },
  // The name opens her Punctuality calendar (the scorecard's section).
  nameBtn: {
    border: 0,
    background: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: 600,
    fontSize: 14,
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': { outline: `2px solid ${C.navy}`, outlineOffset: 2 }
  },
  sub: { color: C.ink2, fontSize: 11.5 },
  num: { textAlign: 'right', fontWeight: 700, paddingRight: 12 },
  bad: { color: C.late },
  cell: {
    borderRadius: 6,
    padding: '4px 6px',
    fontSize: 11.5,
    lineHeight: 1.3
  },
  cellWord: { display: 'block', fontWeight: 700 },
  P: { background: C.presentBg, color: C.present },
  MS: {
    background: `repeating-linear-gradient(45deg, ${C.presentBg}, ${C.presentBg} 5px, #fff 5px, #fff 9px)`,
    color: C.present,
    border: `1px dashed ${C.present}`
  },
  A: { background: C.absentBg, color: C.absent },
  L: { background: C.leaveBg, color: C.leave },
  WO: { color: C.off, border: '1px dashed #ddd' },
  late: { background: C.lateBg, color: C.late, border: `1px solid ${C.late}` },
  X: { background: '#f3f3f3', color: C.ink2 },
  legend: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    marginTop: 12,
    color: C.ink2,
    fontSize: 12
  },
  swatch: {
    display: 'inline-block',
    width: 14,
    height: 14,
    borderRadius: 3,
    marginRight: 6,
    verticalAlign: 'middle'
  },
  sumGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 10,
    margin: '10px 0 6px',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, 1fr)' }
  },
  fig: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    borderRadius: 8,
    border: `1px solid ${C.line}`
  },
  figLink: {
    '&:hover': { borderColor: C.navy, background: '#FAF8F5' },
    '& $tileN': { textDecoration: 'underline dotted' }
  },
  pop: {
    padding: '10px 14px',
    maxHeight: 360,
    overflow: 'auto',
    minWidth: 260
  },
  popRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    fontSize: 12.5,
    padding: '3px 0',
    borderBottom: `1px solid ${C.line}`
  },
  empty: { padding: 30, textAlign: 'center', color: C.ink2 },
  loading: { padding: 60, textAlign: 'center' }
}))

const INFO = (
  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
    From Keka&apos;s <b>Daily Performance</b> report, emailed every morning. It
    always runs up to <b>yesterday</b> — Keka does not export today.
    <br />
    <br />
    <b>Late</b> = clocked in after the shift start in Keka, no buffer. Minutes
    late count from the shift start: a 9:00 shift, in at 9:17, is 17 min late.
    <br />
    <b>Absent</b> = a working day with no clock-in and no leave.
    <br />
    <b>Present, no clock-out</b> = marked present, but Keka has no clock-out.
    <br />
    Weekly offs come from each person&apos;s own Keka roster, so a day off is
    never counted as absent.
  </div>
)

export const centreOf = (e) => {
  if (e.cc === 'Corporate') return 'HQ'
  return e.cc || NO_CENTRE
}

export const classify = (v) => {
  if (!v || !v.s) return { k: 'X', word: 'Not employed' }
  const s = v.s
  const isLate = (v.late || 0) > GRACE_MINUTES
  if (s.includes(':')) {
    const [first, second] = s.split(':')
    return {
      k: 'L',
      word: `Half day · ${LEAVE[first] || LEAVE[second] || s}`,
      time: v.in
    }
  }
  if (s === 'WO') return { k: 'WO', word: 'Weekly off' }
  if (s === 'H') return { k: 'WO', word: 'Holiday' }
  if (LEAVE[s]) return { k: 'L', word: LEAVE[s] }
  if (s === 'A') return { k: 'A', word: 'Absent' }
  if (s === 'WOW')
    return {
      k: isLate ? 'late' : 'P',
      word: 'Worked on off day',
      time: v.in,
      late: lateAfterGrace(v)
    }
  if (s === 'P(MS)')
    return {
      k: isLate ? 'late' : 'MS',
      word: 'Present, no clock-out',
      time: v.in,
      late: lateAfterGrace(v)
    }
  if (s === 'P' || s.startsWith('P'))
    return {
      k: isLate ? 'late' : 'P',
      word: 'Present',
      time: v.in,
      late: lateAfterGrace(v)
    }
  return { k: 'X', word: `Keka code "${s}"` }
}

const countWeek = (e, dates) => {
  const c = { late: 0, absent: 0, present: 0, noOut: 0 }
  dates.forEach((d) => {
    const x = classify(e.days[d])
    if (x.k === 'late') {
      c.late += 1
      c.present += 1
    } else if (x.k === 'P') c.present += 1
    else if (x.k === 'MS') {
      c.present += 1
      c.noOut += 1
    } else if (x.k === 'A') c.absent += 1
  })
  return c
}

// One person's figures for the dates on screen, each kept as its list of
// dates so a figure can show what is behind it. Same day rules as the
// Punctuality calendar under it (scoring.js dayFor / missedClass, Satya
// 26 Oct): the preschool calendar's holidays are off, a CD's Saturday is a
// working day, and absent / leave counts only on a day she had a class.
// Expected = days at work + those missed days; everything else is off.
// Not linked to an Alfred teacher: every absent and leave day counts.
export const personSummary = (e, dates) => {
  const t = e.class_days === undefined ? { ...e, class_days: null } : e
  const s = { expected: [], off: [], present: [], onTime: [], late: [] }
  dates.forEach((d) => {
    const k = dayFor(t, d)
    if (k === 'none') return
    if (k === 'on' || k === 'late') {
      s.expected.push(d)
      s.present.push(d)
      s[k === 'on' ? 'onTime' : 'late'].push(d)
    } else if (missedClass(t, d)) s.expected.push(d)
    else s.off.push(d)
  })
  return s
}

// What a day in a figure's list was, in the Punctuality calendar's words.
const offWord = (t, d) => {
  const k = dayFor(t, d)
  if (k === 'holiday') return 'Holiday (preschool calendar)'
  if (k === 'absent' || k === 'leave')
    return `${k === 'leave' ? 'Leave' : 'Absent'} · no class`
  return null
}

const toIso = (d) => {
  const pad = (x) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const shiftDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + n)
  return toIso(d)
}

// The dates a time frame stands for, counted from Keka's latest day.
export const rangeFor = (key, latest) => {
  const last = new Date(`${latest}T00:00:00`)
  if (key === 'month')
    return {
      start: toIso(new Date(last.getFullYear(), last.getMonth(), 1)),
      end: latest
    }
  if (key === 'lastMonth')
    return {
      start: toIso(new Date(last.getFullYear(), last.getMonth() - 1, 1)),
      end: toIso(new Date(last.getFullYear(), last.getMonth(), 0))
    }
  return { start: shiftDays(latest, 1 - Number(key)), end: latest }
}

const daysBetween = (a, b) =>
  Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 864e5)

// The same number of days, one step earlier (dir -1) or later (dir 1). A step
// that would run past Keka's first or latest day stops there, keeping the
// length where the data allows.
export const stepSpan = (from, to, dir, first, latest) => {
  const len = daysBetween(from, to) + 1
  let start = shiftDays(from, dir * len)
  let end = shiftDays(to, dir * len)
  if (latest && end > latest) {
    end = latest
    start = shiftDays(latest, 1 - len)
  }
  if (first && start < first) {
    start = first
    end = shiftDays(first, len - 1)
    if (latest && end > latest) end = latest
  }
  return { start, end }
}

const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })

const shortDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short'
  })

function DayCell({ v, classes, mini }) {
  const x = classify(v)
  const word = x.k === 'late' ? 'Late' : x.word
  let sub = null
  if (x.time)
    sub = `In ${x.time}${x.k === 'late' ? ` · ${x.late} min late` : ''}`
  const shift = [
    v && v.st && `shift starts ${v.st}`,
    v && v.out && `out ${v.out}`
  ]
  const detail = [x.word, ...shift].filter(Boolean).join(' · ')
  // Long ranges: a coloured square with a letter; the full story (status,
  // clock-in, minutes late) is in the hover, each part once ("Absent ·
  // Absent" before, 22 Oct).
  if (mini)
    return (
      <Tooltip
        title={[
          word,
          x.k === 'late' && x.word !== 'Present' ? x.word : null,
          sub,
          ...shift
        ]
          .filter(Boolean)
          .join(' · ')}
      >
        <div className={`${classes.mini} ${classes[x.k]}`}>{SHORT[x.k]}</div>
      </Tooltip>
    )
  return (
    <Tooltip title={detail} placement="top">
      <div className={`${classes.cell} ${classes[x.k]}`}>
        <span className={classes.cellWord}>{word}</span>
        {sub}
      </div>
    </Tooltip>
  )
}

// One person's attendance figures (Shruti, Head of Preschool, 2026-10-22),
// shown at the top of the pop-up a name opens, for the period picked there
// (Satya, 22 Oct). Each count opens its dates.
function PersonSummary({ e, dates, classes }) {
  const [open, setOpen] = useState(null)
  const s = useMemo(() => personSummary(e, dates), [e, dates])
  const pct = s.expected.length
    ? `${Math.round((s.present.length / s.expected.length) * 100)}%`
    : '–'
  const figures = [
    ['expected', 'Expected working days'],
    ['off', 'Days off (week off, holiday, no class)'],
    ['present', 'Days present'],
    ['pct', 'Attendance %'],
    ['onTime', 'Days on time'],
    ['late', 'Days late']
  ]
  return (
    <div>
      <div className={classes.sumGrid}>
        {figures.map(([key, label]) => {
          const body = (
            <>
              <div className={classes.tileN}>
                {key === 'pct' ? pct : s[key].length}
              </div>
              <div className={classes.tileL}>{label}</div>
            </>
          )
          // Attendance % and empty counts have nothing to list, so they are
          // plain figures, not buttons.
          if (key === 'pct' || !s[key].length)
            return (
              <div key={key} className={classes.fig}>
                {body}
              </div>
            )
          return (
            <ButtonBase
              key={key}
              className={`${classes.fig} ${classes.figLink}`}
              onClick={(ev) => setOpen({ key, label, at: ev.currentTarget })}
            >
              {body}
            </ButtonBase>
          )
        })}
      </div>
      <div className={classes.sub}>
        Attendance % = days present ÷ expected working days. Expected = days at
        work + absent or leave on a working day (a CD, DCD or AM works Saturdays
        too). Part-timers (Teacher Community): absent or leave counts only on a
        day with a class. Week offs and preschool calendar holidays are off.
        Click a number for its dates.
      </div>
      <Popover
        open={Boolean(open)}
        anchorEl={open && open.at}
        onClose={() => setOpen(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {open && (
          <div className={classes.pop}>
            <div className={classes.name}>
              {open.label} · {s[open.key].length}
            </div>
            {s[open.key].map((d) => {
              const v = e.days[d]
              const x = classify(v)
              const when = [
                x.time && `In ${x.time}`,
                x.k === 'late' && `${x.late} min late`
              ]
                .filter(Boolean)
                .join(' · ')
              const word = (open.key === 'off' && offWord(e, d)) || x.word
              return (
                <div key={d} className={classes.popRow}>
                  <span>{dayLabel(d)}</span>
                  <span className={classes.sub}>
                    {[word, when].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </Popover>
    </div>
  )
}

function Who({ e, classes, stick, onOpen }) {
  return (
    <td
      className={`${classes.td} ${classes.who} ${stick ? classes.stick : ''}`}
    >
      <Tooltip title="See punctuality calendar">
        <button
          type="button"
          className={classes.nameBtn}
          onClick={() => onOpen(e)}
        >
          {e.name}
        </button>
      </Tooltip>
      <div className={classes.sub}>
        {e.no} · {centreOf(e)} · {e.job || e.dept}
      </div>
    </td>
  )
}

export default function TeacherAttendance() {
  const classes = useStyles()
  const calendar = useCalendarDaysOff()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [range, setRange] = useState('7')
  // What the page asks the server for. Empty = "the latest week", which is
  // also how the page learns Keka's first and latest day on first load.
  const [span, setSpan] = useState({})
  const [centre, setCentre] = useState('')
  const [open, setOpen] = useState(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    getKekaAttendanceWeek(span)
      .then((d) => !cancelled && setData(d))
      .catch((err) => {
        if (cancelled) return
        const detail =
          err && err.response && err.response.data && err.response.data.detail
        setError(detail || 'Could not load attendance. Please refresh.')
      })
    return () => {
      cancelled = true
    }
  }, [span])

  // Holidays come from the Annual Calendar; until it has loaded, show nothing
  // rather than days counted against the wrong calendar.
  const employees = (calendar !== 'loading' && data && data.employees) || []
  const dates = (data && data.dates) || []

  const field = useMemo(() => employees.filter(onPage), [employees])

  const centres = useMemo(() => {
    const names = [...new Set(field.map(centreOf))]
    const last = (c) => (c === NO_CENTRE ? 1 : 0)
    return names.sort((a, b) => last(a) - last(b) || a.localeCompare(b))
  }, [field])

  const people = useMemo(() => {
    const q = query.trim().toLowerCase()
    return field.filter(
      (e) =>
        (!centre || centreOf(e) === centre) &&
        (!q ||
          e.name.toLowerCase().includes(q) ||
          e.no.toLowerCase().includes(q))
    )
  }, [field, centre, query])

  // Lives in the Person header of both tables (as on Teacher Scorecard); the
  // column name sits inside the box so the header stays one row tall.
  const personSearch = (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      placeholder="Person (search name or no.)"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      className={classes.headSearch}
      inputProps={{ 'aria-label': 'Search people' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="disabled" />
          </InputAdornment>
        )
      }}
    />
  )

  const first = data && data.earliest_date
  const latest = data && data.latest_date
  // Keep showing the chosen dates while the next range loads.
  const from = span.start || (data && data.start) || ''
  const to = span.end || (data && data.end) || ''
  const wide = dates.length > WIDE_DAYS

  const pickRange = (key) => {
    setRange(key)
    if (key !== 'custom' && latest) setSpan(rangeFor(key, latest))
  }
  // Typing a date makes it a custom range. The other end follows if the two
  // would cross or stretch past what the server allows.
  const pickDate = (which, value) => {
    if (!value) return
    let start = which === 'start' ? value : from
    let end = which === 'end' ? value : to
    if (start > end) {
      if (which === 'start') end = start
      else start = end
    }
    if (daysBetween(start, end) >= MAX_DAYS) {
      if (which === 'start') end = shiftDays(start, MAX_DAYS - 1)
      else start = shiftDays(end, 1 - MAX_DAYS)
    }
    setRange('custom')
    setSpan({ start, end })
  }
  // The arrows move the dates on screen back or forward by their own length.
  const shown = (data && data.start) || from
  const canBack = Boolean(first && shown && shown > first)
  const canForward = Boolean(latest && to && to < latest)
  const step = (dir) => {
    if (!from || !to) return
    setRange('custom')
    setSpan(stepSpan(from, to, dir, first, latest))
  }
  const days = from && to ? daysBetween(from, to) + 1 : 7
  const stepWord = days === 7 ? 'week' : `${days} days`
  // A time frame can reach back before Keka's first day; say so rather than
  // quietly showing fewer days than the label promises.
  const cutShort =
    data && span.start && data.start > span.start
      ? `Keka data starts on ${dayLabel(first)}, so this shows ${shortDate(
          data.start
        )} – ${shortDate(data.end)}.`
      : null

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <Typography variant="h5">Attendance</Typography>
        <Tooltip title={INFO} placement="bottom-start" interactive>
          <InfoOutlinedIcon className={classes.info} />
        </Tooltip>
      </div>

      <div className={classes.bar}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Time frame"
          className={classes.range}
          value={range}
          onChange={(e) => pickRange(e.target.value)}
        >
          {RANGES.map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <div className={classes.step}>
          <Tooltip title={`Previous ${stepWord}`}>
            <span>
              <IconButton
                size="small"
                className={classes.arrow}
                aria-label={`Previous ${stepWord}`}
                disabled={!canBack}
                onClick={() => step(-1)}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            type="date"
            size="small"
            variant="outlined"
            label="From"
            className={classes.date}
            value={from}
            onChange={(e) => pickDate('start', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: first || undefined, max: latest || undefined }}
          />
          <TextField
            type="date"
            size="small"
            variant="outlined"
            label="To"
            className={classes.date}
            value={to}
            onChange={(e) => pickDate('end', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: first || undefined, max: latest || undefined }}
          />
          <Tooltip title={`Next ${stepWord}`}>
            <span>
              <IconButton
                size="small"
                className={classes.arrow}
                aria-label={`Next ${stepWord}`}
                disabled={!canForward}
                onClick={() => step(1)}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <TextField
          select
          size="small"
          variant="outlined"
          className={classes.select}
          value={centre}
          onChange={(e) => setCentre(e.target.value)}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All centres</MenuItem>
          {centres.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      </div>

      {error && (
        <Paper className={classes.empty} elevation={0}>
          {error}
        </Paper>
      )}
      {!error && (!data || calendar === 'loading') && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
      {!error && data && !dates.length && (
        <Paper className={classes.empty} elevation={0}>
          No attendance has arrived from Keka yet.
        </Paper>
      )}

      {!error && data && dates.length > 0 && (
        <>
          {cutShort && <div className={classes.hint}>{cutShort}</div>}
          <Paper
            className={`${classes.card} ${wide ? classes.scroll : ''}`}
            elevation={0}
          >
            <table
              className={classes.table}
              style={wide ? { minWidth: 362 + dates.length * 40 } : undefined}
            >
              <colgroup>
                <col style={{ width: 230 }} />
                {dates.map((d) => (
                  <col key={d} style={wide ? { width: 40 } : undefined} />
                ))}
                <col style={{ width: 62 }} />
                <col style={{ width: 70 }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    className={classes.th}
                    style={wide ? { left: 0, zIndex: 3 } : undefined}
                  >
                    {personSearch}
                  </th>
                  {dates.map((d) => (
                    <th
                      key={d}
                      className={`${classes.th} ${wide ? classes.thDay : ''}`}
                    >
                      {wide ? (
                        <Tooltip title={dayLabel(d)} placement="top">
                          <span>
                            {dayLabel(d).slice(0, 2)}
                            <br />
                            {Number(d.slice(8))}
                          </span>
                        </Tooltip>
                      ) : (
                        dayLabel(d)
                      )}
                    </th>
                  ))}
                  <th className={classes.th}>Late</th>
                  <th className={classes.th}>Absent</th>
                </tr>
              </thead>
              <tbody>
                {people.length === 0 && (
                  <tr>
                    <td className={classes.empty} colSpan={dates.length + 3}>
                      No one matches these filters.
                    </td>
                  </tr>
                )}
                {people.map((e) => {
                  const c = countWeek(e, dates)
                  return (
                    <tr key={e.no}>
                      <Who
                        e={e}
                        classes={classes}
                        stick={wide}
                        onOpen={setOpen}
                      />
                      {dates.map((d) => (
                        <td
                          key={d}
                          className={`${classes.td} ${
                            wide ? classes.tdMini : ''
                          }`}
                        >
                          <DayCell
                            v={e.days[d]}
                            classes={classes}
                            mini={wide}
                          />
                        </td>
                      ))}
                      <td
                        className={`${classes.td} ${classes.num} ${
                          c.late >= 3 ? classes.bad : ''
                        }`}
                      >
                        {c.late}
                      </td>
                      <td className={`${classes.td} ${classes.num}`}>
                        {c.absent}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Paper>

          <div className={classes.legend}>
            {wide && (
              <span>
                Ranges longer than {WIDE_DAYS} days show one square per day —
                hover a square for clock-in and minutes late.
              </span>
            )}
            {[
              ['P', 'Present'],
              ['late', 'Late'],
              ['MS', 'Present, no clock-out'],
              ['A', 'Absent'],
              ['L', 'Leave'],
              ['WO', 'Weekly off']
            ].map(([k, label]) => (
              <span key={k}>
                <i className={`${classes.swatch} ${classes[k]}`} />
                {label}
              </span>
            ))}
          </div>
        </>
      )}
      {open && (
        <PunctualityDialog
          person={open}
          centre={centreOf(open)}
          onClose={() => setOpen(null)}
          attendance={(e, days) => (
            <PersonSummary e={e} dates={days} classes={classes} />
          )}
        />
      )}
    </div>
  )
}
